// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Morning briefing card — pinned at the top of Ask LodgIQ when the user first
// opens a property each day. Narrative streams in; the facts strip and source
// chips deep-link to the dashboard. Design: MORNING_BRIEFING_DESIGN.md.

import React from "react";

const nice = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
};

export default function BriefingCard({
  briefing,          // ready briefing JSON (or null while streaming)
  streamText,        // accumulating narrative while generating
  streaming,         // bool
  hotelName,
  onAskFollowUp,     // () => void
  onDismiss,         // () => void
  onFeedback,        // (fb) => void
  onOpenSource,      // (srcRef) => void
}) {
  const facts = (briefing && briefing.facts) || {};
  const outlook = facts.outlook || {};
  const tonight = outlook.tonight || {};
  const behind = outlook.most_behind_ly;
  const events = facts.events || [];
  const failures = facts.push_failures || [];
  const date = (briefing && briefing.date) || facts.date;
  const text = briefing ? briefing.narrative : streamText;

  return (
    <div className="wiz-brief">
      <div className="wiz-brief__head">
        <span className="wiz-brief__sun" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 18a5 5 0 0 0-10 0" />
            <path d="M12 9V4M4.2 12.2l1.4 1.4M1 18h2M21 18h2M18.4 13.6l1.4-1.4" />
            <path d="M3 22h18" />
          </svg>
        </span>
        <div className="wiz-brief__titles">
          <span className="wiz-brief__title">Morning briefing</span>
          {/* Always one hotel. AL-94 gave the portfolio its own card, so this one
              no longer has to explain which scope it belongs to. */}
          <span className="wiz-brief__sub">
            {hotelName ? `${hotelName} · ` : ""}{date ? nice(date) : ""}
          </span>
        </div>
        {onDismiss && (
          <button className="wiz-brief__close" onClick={onDismiss} aria-label="Dismiss briefing" title="Dismiss for today">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        )}
      </div>

      <div className="wiz-brief__body">
        {text ? text.split(/\n+/).map((p, i) => <p key={i}>{p}</p>) : null}
        {streaming && (
          <span className="wiz-typing wiz-brief__typing"><span /><span /><span /></span>
        )}
      </div>

      {briefing && (
        <>
          <div className="wiz-brief__facts">
            {tonight.occ_pct != null && (
              <span className="wiz-brief__fact">
                <b>{Math.round(tonight.occ_pct)}%</b> tonight
              </span>
            )}
            {outlook.avg_occ_7d_pct != null && (
              <span className="wiz-brief__fact"><b>{Math.round(outlook.avg_occ_7d_pct)}%</b> avg next 7d</span>
            )}
            {behind && behind.gap_pts < 0 && (
              <span className="wiz-brief__fact wiz-brief__fact--warn">
                <b>{behind.gap_pts} pts</b> vs LY on {behind.date && behind.date.slice(5)}
              </span>
            )}
            {failures.length > 0 && (
              <span className="wiz-brief__fact wiz-brief__fact--bad"><b>{failures.length}</b> push failure{failures.length === 1 ? "" : "s"}</span>
            )}
            {events.length > 0 && events[0].title && (
              <span className="wiz-brief__fact wiz-brief__fact--ev">{events[0].title}</span>
            )}
          </div>

          {Array.isArray(briefing.sources) && briefing.sources.length > 0 && onOpenSource && (
            <div className="wiz-brief__sources">
              {briefing.sources.map((s, i) => (
                <button key={i} className="wiz-source-chip" onClick={() => onOpenSource(s)}>
                  {s.label} ↗
                </button>
              ))}
            </div>
          )}

          <div className="wiz-brief__actions">
            {onAskFollowUp && (
              <button className="wiz-brief__follow" onClick={onAskFollowUp}>Ask a follow-up</button>
            )}
            {onFeedback && (
              <span className="wiz-fb">
                <button
                  className={`wiz-fb__btn ${briefing.feedback === "up" ? "is-on is-up" : ""}`}
                  onClick={() => onFeedback(briefing.feedback === "up" ? "" : "up")}
                  aria-label="Useful briefing"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3zM7 10l4.2-7a2.3 2.3 0 0 1 2.3 2.3V9h5.1a2 2 0 0 1 2 2.4l-1.4 7a2 2 0 0 1-2 1.6H7" />
                  </svg>
                </button>
                <button
                  className={`wiz-fb__btn ${briefing.feedback === "down" ? "is-on is-down" : ""}`}
                  onClick={() => onFeedback(briefing.feedback === "down" ? "" : "down")}
                  aria-label="Not useful"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3zM17 14l-4.2 7a2.3 2.3 0 0 1-2.3-2.3V15H5.4a2 2 0 0 1-2-2.4l1.4-7a2 2 0 0 1 2-1.6H17" />
                  </svg>
                </button>
              </span>
            )}
            {briefing.context_as_of && (
              <span className="wiz-brief__asof">data as of {String(briefing.context_as_of).slice(0, 10)}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
